import { useState, useEffect } from "react";
import toast from "react-hot-toast";
import ILeadData, {
  IAddress,
  ICategory,
  ILeadSource,
  ISourceType,
} from "../../../models/LeadData.model";
import InputField from "./InputField";
import SelectField from "./SelectedField";
import AddressSection from "./addressSection";
import Button, { ButtonType } from "../../../components/Assets/Button";
import { RootDispatch, RootState } from "../../../store";
import { useDispatch, useSelector } from "react-redux";
import { getStatesData } from "../../../store/slices/kanbanSlice";
import {
  setUpdateOutsideModal,
  setUpdateOutsideModalFalse,
} from "../../../store/slices/leadSlice";
import { set } from "lodash";

interface EditLeadProps {
  card: ILeadData;
  setModalOpen?: (isOpen: boolean) => void;
  submitBtnText?: string;
  callApi: (data: Partial<ILeadData>) => Promise<void>;
  inline?: boolean;
  role?: string;
  className?: string;
  forTelecaller?: boolean;
  mobileNumberTelecaller?: string;
  setMobileNumber?: (number: string) => void;
  skipped?: boolean;
  notInterested?: boolean;
}

const EditLead: React.FC<EditLeadProps> = ({
  forTelecaller = false,
  card,
  setModalOpen,
  submitBtnText: btnText,
  callApi,
  inline = false,
  setMobileNumber,
  mobileNumberTelecaller,
  skipped = false,
  notInterested = false,
}) => {
  const dispatch = useDispatch<RootDispatch>();
  const statesData = useSelector((state: RootState) => state.kanban.statesData);
  const isTelecallerCallToday = useSelector(
    (state: RootState) => state.telecallerLead.isTeleCallerCallsToday
  );
  const callLogs = useSelector(
    (state: RootState) => state.telecallerLead.callLogs
  );
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [whatsappNumber, setWhatsappNumber] = useState("");
  const [mobileNumberLead, setMobileNumberLead] = useState("");
  const [sameAsMobile, setSameAsMobile] = useState(false);
  const [source, setSource] = useState("");
  const [type, setType] = useState("");
  const [category, setCategory] = useState("");
  const [contactPersonName, setContactPersonName] = useState("");
  const [requirements, setRequirements] = useState("");

  const [address, setAddress] = useState<IAddress>({
    line1: "",
    city: "",
    state: "",
    district: "",
    pincode: "",
  });
  useEffect(() => {
    if (card) {
      setName(card.name || "");
      setEmail(card.email || "");
      setMobileNumberLead(card.mobileNumber || "");
      setWhatsappNumber(card.whatsappNumber || "");
      setSameAsMobile(card.whatsappNumber === card.mobileNumber);
      setSource(card.source || "");
      setType(card.type || "");
      setCategory(card.category || "");
      setContactPersonName(card.contactPersonName || "");
      setRequirements(card.requirements || "");
      setAddress(
        card.address || {
          line1: "",
          city: "",
          state: "",
          district: "",
          pincode: "",
        }
      );
    }
  }, [card]);

  useEffect(() => {
    if (statesData.length === 0) {
      dispatch(getStatesData());
    }
  }, [dispatch, statesData.length]);
  const getMobileNumber = () => {
    return mobileNumberTelecaller ?? mobileNumberLead;
  };
  const handleFormSubmit = async () => {
    if (
      !name.trim() ||
      !getMobileNumber()?.trim() ||
      !source.trim() ||
      !type.trim() ||
      !category.trim()
    ) {
      return toast.error("Please fill all the required fields");
    }

    if (!/^\d{10}$/.test(getMobileNumber()!)) {
      return toast.error("Mobile number must be 10 digits.");
    }

    if (!/^\d{10}$/.test(getMobileNumber()!)) {
      toast.error("Mobile number must be 10 digits.");
      return;
    }

    // if (address && address.pincode !== "" && !/^\d{6}$/.test(address.pincode)) {
    //   return toast.error("Pincode must be 6 digits.");
    // }

    if (!address.state?.trim() || !address.district?.trim()) {
      return toast.error("Please fill both State and District in the address");
    }

    const updatedData = {
      name,
      email,
      mobileNumber: getMobileNumber(),
      whatsappNumber: sameAsMobile ? getMobileNumber() : whatsappNumber,
      source: source as ILeadSource,
      type: type as ISourceType,
      category: category as ICategory,
      contactPersonName,
      requirements,
      address,
      // showToTelecaller,
    };
    dispatch(setUpdateOutsideModalFalse());
    await callApi(updatedData);
  };

  return (
    <div
      className={`${
        inline
          ? ""
          : "fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[99] p-6"
      }`}
    >
      <div
        className={`bg-white backdrop-blur-md border border-white/20 shadow-2xl rounded-2xl p-8 w-full max-w-3xl relative ${
          inline
            ? "static shadow-none border-none p-0 max-w-full overflow-visible max-h-full"
            : "max-h-[90vh] overflow-y-auto"
        }`}
      >
        {/* Header */}
        <div className="flex justify-between items-center border-b border-gray-300/40 pb-4 mb-6">
          <h2 className="text-lg md:text-xl font-semibold text-gray-700 tracking-wide">
            Edit Lead
          </h2>
          {!inline && setModalOpen && (
            <button
              onClick={() => setModalOpen(false)}
              className="text-gray-300 hover:text-gray-500 text-2xl transition-all"
            >
              ✖
            </button>
          )}
        </div>

        <form className="space-y-6 text-xs">
          <div className="grid grid-cols-1  sm:grid-cols-2 gap-x-6 gap-y-2">
            <InputField
              label="Name"
              value={name}
              setValue={setName}
              placeholder="Enter name"
            />
            <InputField
              label="Email"
              value={email}
              setValue={setEmail}
              placeholder="Enter email"
            />
            <InputField
              label="Mobile Number"
              value={getMobileNumber()}
              setValue={setMobileNumber ?? setMobileNumberLead}
              placeholder="Enter mobile number"
            />
            <SelectField
              label="Source"
              value={source}
              setValue={setSource}
              options={Object.values(ILeadSource)}
            />

            {!sameAsMobile && (
              <InputField
                label="Whatsapp Number"
                value={whatsappNumber}
                setValue={setWhatsappNumber}
                placeholder="Enter whatsapp number"
              />
            )}
            <div className="flex items-center space-x-2">
              <input
                id="sameAsMobile"
                type="checkbox"
                checked={sameAsMobile}
                onChange={(e) => setSameAsMobile(e.target.checked)}
                className="form-checkbox h-4 w-4 text-blue-600"
              />
              <label htmlFor="sameAsMobile" className="text-sm text-gray-700">
                Same as Mobile Number
              </label>
            </div>

            <SelectField
              label="Type"
              value={type}
              setValue={setType}
              options={Object.values(ISourceType)}
            />
            <SelectField
              label="Category"
              value={category}
              setValue={setCategory}
              options={Object.values(ICategory)}
            />
            <InputField
              label="Contact Person Name"
              value={contactPersonName}
              setValue={setContactPersonName}
              placeholder="Enter contact person name"
            />
          </div>

          <InputField
            label="Requirements"
            value={requirements}
            setValue={setRequirements}
            placeholder="Enter requirements"
          />

          <details className="bg-gray-100 p-4 rounded-lg">
            <summary className="text-gray-700 font-semibold cursor-pointer">
              Primary Address
            </summary>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-3 ">
              <AddressSection
                address={address}
                setAddress={setAddress}
                statesData={statesData}
              />
            </div>
          </details>

          <div className="flex flex-col md:flex-row gap-2 justify-end">
            {(isTelecallerCallToday && forTelecaller) ||
            skipped ||
            notInterested ? (
              <Button
                type={ButtonType.Submit}
                onClick={handleFormSubmit}
                className="text-xs md:text-sm"
              >
                {btnText ?? "Update"}
              </Button>
            ) : null}
            {!forTelecaller && (
              <Button
                type={ButtonType.Submit}
                onClick={handleFormSubmit}
                className="text-xs md:text-sm"
              >
                {btnText ?? "Update"}
              </Button>
            )}

            {!inline && setModalOpen && (
              <Button
                onClick={() => setModalOpen(false)}
                type={ButtonType.Cancel}
                className="text-xs md:text-sm"
              >
                Close
              </Button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditLead;
